import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Input, Progress, Space, Table, Typography, message } from 'antd';
import { StopOutlined, SyncOutlined } from '@ant-design/icons';
import { axiosInstance } from '../../config';

const { Text, Title } = Typography;

const parseArticles = (value) =>
    String(value || '')
        .split(/[\s,;]+/)
        .map((item) => item.trim())
        .filter(Boolean);

const OzonProductSync = () => {
    const [articles, setArticles] = useState('');
    const [starting, setStarting] = useState(false);
    const [sync, setSync] = useState({
        status: 'idle',
        processed: 0,
        total: 0,
        progress: 0,
        created: 0,
        updated: 0,
        failed: 0,
        errors: [],
        last_offer_id: '',
        sync_all: false,
    });
    const previousStatusRef = useRef('idle');

    const applyStatus = useCallback((next) => {
        const previous = previousStatusRef.current;
        setSync(next);
        previousStatusRef.current = next.status;
        if (previous === 'running' && next.status === 'completed') {
            message.success(
                `Синхронизация завершена: ${next.created || 0} новых, ${next.updated || 0} обновлено`
            );
        } else if (previous === 'running' && next.status === 'failed') {
            message.error(next.error || 'Синхронизация завершилась с ошибкой');
        } else if (previous === 'running' && next.status === 'cancelled') {
            message.info('Синхронизация остановлена');
        }
    }, []);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/v1/ozon-products/sync/status');
            applyStatus(data);
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось получить статус синхронизации'
            );
        }
    }, [applyStatus]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    useEffect(() => {
        if (sync.status !== 'running') return undefined;
        const timer = window.setInterval(loadStatus, 1500);
        return () => window.clearInterval(timer);
    }, [loadStatus, sync.status]);

    const startSync = async (syncAll) => {
        const offerIds = parseArticles(articles);
        if (!syncAll && !offerIds.length) {
            message.warning('Укажите артикулы, по одному в строке');
            return;
        }
        setStarting(true);
        try {
            const { data } = await axiosInstance.post('/v1/ozon-products/sync', {
                offer_ids: offerIds,
                sync_all: syncAll,
            });
            applyStatus(data);
            if (data.started) {
                message.loading({
                    content: syncAll
                        ? 'Синхронизация всех товаров Ozon запущена'
                        : `Синхронизация ${offerIds.length} артикулов запущена`,
                    key: 'ozon-product-sync',
                    duration: 2,
                });
            } else {
                message.info('Синхронизация уже выполняется');
            }
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось запустить синхронизацию'
            );
        } finally {
            setStarting(false);
        }
    };

    const stopSync = async () => {
        try {
            const { data } = await axiosInstance.post('/v1/ozon-products/sync/stop');
            applyStatus(data);
            message.info('Запрошена остановка синхронизации');
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось остановить синхронизацию'
            );
        }
    };

    const isRunning = sync.status === 'running';
    const parsedCount = parseArticles(articles).length;
    const statusText = isRunning
        ? sync.cancel_requested
            ? 'Останавливаем синхронизацию…'
            : `Обработано ${sync.processed || 0}${sync.total ? ` из ${sync.total}` : ''}${
                  sync.last_offer_id ? ` · ${sync.last_offer_id}` : ''
              }`
        : sync.status === 'completed'
          ? `Готово: ${sync.created || 0} новых, ${sync.updated || 0} обновлено, ${sync.failed || 0} ошибок`
          : sync.status === 'cancelled'
            ? `Остановлено после ${sync.processed || 0} товаров`
            : 'Синхронизация ещё не запускалась';

    return (
        <div className="p-6">
            <Card>
                <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <Title level={3} className="!mb-1">
                            Синхронизация характеристик Ozon
                        </Title>
                        <Text type="secondary">
                            Укажите артикулы как раньше в temparr — по одному в строке. По ним
                            подтянутся название, цвет, размер, цена, фото и PDF-этикетка в остатки.
                        </Text>
                    </div>
                    <Space wrap>
                        <Button
                            type="primary"
                            icon={<SyncOutlined />}
                            loading={starting || isRunning}
                            onClick={() => startSync(false)}
                        >
                            Синхронизировать указанные
                        </Button>
                        <Button
                            icon={<SyncOutlined />}
                            loading={starting || isRunning}
                            onClick={() => startSync(true)}
                        >
                            Все товары Ozon
                        </Button>
                        {isRunning && (
                            <Button danger icon={<StopOutlined />} onClick={stopSync}>
                                Остановить
                            </Button>
                        )}
                    </Space>
                </div>

                <Input.TextArea
                    value={articles}
                    onChange={(event) => setArticles(event.target.value)}
                    placeholder={'BN-PRO-171\nHB-EXT-202\nHB-PRO-202'}
                    autoSize={{ minRows: 8, maxRows: 18 }}
                    disabled={isRunning}
                />
                <div className="mt-2 text-sm text-slate-500">
                    Артикулов в списке: {parsedCount}
                </div>

                <div className="mt-6">
                    <div className="mb-2 flex items-center justify-between gap-4">
                        <Text>{statusText}</Text>
                        {sync.sync_all && isRunning && <Text type="secondary">Все товары</Text>}
                    </div>
                    <Progress
                        percent={sync.progress || 0}
                        status={
                            sync.status === 'failed'
                                ? 'exception'
                                : isRunning
                                  ? 'active'
                                  : sync.status === 'completed'
                                    ? 'success'
                                    : 'normal'
                        }
                    />
                </div>

                {sync.status === 'failed' && (
                    <Alert
                        className="mt-4"
                        type="error"
                        showIcon
                        message="Синхронизация остановлена"
                        description={sync.error || 'Неизвестная ошибка'}
                    />
                )}

                {Array.isArray(sync.errors) && sync.errors.length > 0 && (
                    <Table
                        className="mt-6"
                        size="small"
                        pagination={false}
                        rowKey={(_, index) => String(index)}
                        dataSource={sync.errors.map((item) => ({ message: item }))}
                        columns={[
                            {
                                title: 'Ошибки по артикулам',
                                dataIndex: 'message',
                            },
                        ]}
                    />
                )}
            </Card>
        </div>
    );
};

export default OzonProductSync;
